import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const maxAttempts = 5;

  const getUrlParameter = (name) => {
    const params = new URLSearchParams(location.search);
    return params.get(name);
  };

  const checkPaymentStatus = async (sessionId, currentAttempt = 0) => {
    if (currentAttempt >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = response.data;
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
      } else if (data.status === 'expired') {
        setStatus('expired');
      } else {
        setAttempts(currentAttempt + 1);
        setTimeout(() => {
          checkPaymentStatus(sessionId, currentAttempt + 1);
        }, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  useEffect(() => {
    const sessionId = getUrlParameter('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      navigate('/payment');
    }
  }, []);

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <div className="text-center" data-testid="payment-checking">
            <Loader2 className="w-16 h-16 text-secondary-main animate-spin mx-auto mb-6" />
            <h2 className="font-heading text-3xl font-bold text-primary-main mb-4">
              Verifying Your Payment
            </h2>
            <p className="text-text-secondary">
              Please wait while we confirm your transaction...
            </p>
            {attempts > 0 && (
              <p className="text-text-muted text-sm mt-2">
                Attempt {attempts + 1} of {maxAttempts}
              </p>
            )}
          </div>
        );

      case 'success':
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
            data-testid="payment-success"
          >
            <div className="bg-green-100 rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="font-heading text-4xl font-bold text-primary-main mb-4">
              Payment Successful!
            </h2>
            <p className="text-text-secondary leading-relaxed mb-8" data-testid="cta-description">
              Thank you for choosing GrowthPop We're excited to help grow your LinkedIn presence.
            </p>
            {paymentData && (
              <div className="bg-white border border-primary-main/10 p-6 max-w-md mx-auto mb-8 text-left">
                <h3 className="font-body font-semibold text-primary-main mb-4">Payment Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-text-muted">Amount:</span>
                    <span className="text-text-primary font-semibold">
                      ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted">Status:</span>
                    <span className="text-green-600 font-semibold">Paid</span>
                  </div>
                  {paymentData.metadata?.package_name && (
                    <div className="flex justify-between">
                      <span className="text-text-muted">Package:</span>
                      <span className="text-text-primary font-semibold">{paymentData.metadata.package_name}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            <p className="text-text-secondary mb-8">
              Our team will contact you within 24 hours to kick off your LinkedIn marketing campaign.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/"
                data-testid="success-home-button"
                className="bg-primary-main text-white hover:bg-primary-light rounded-none px-8 py-4 uppercase tracking-widest text-xs font-semibold transition-all"
              >
                Back to Home
              </a>
              <a
                href="/enquiry"
                data-testid="success-contact-button"
                className="border border-primary-main text-primary-main hover:bg-primary-main hover:text-white rounded-none px-8 py-4 uppercase tracking-widest text-xs font-semibold transition-all"
              >
                Contact Us
              </a>
            </div>
          </motion.div>
        );

      case 'expired':
      case 'timeout':
      case 'error':
        return (
          <div className="text-center" data-testid="payment-error">
            <div className="bg-red-100 rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center">
              <span className="text-4xl">⚠️</span>
            </div>
            <h2 className="font-heading text-3xl font-bold text-primary-main mb-4">
              {status === 'expired' ? 'Payment Session Expired' : 'Payment Verification Issue'}
            </h2>
            <p className="text-text-secondary mb-8">
              {status === 'expired'
                ? 'Your payment session has expired. Please try again.'
                : 'We couldn\'t verify your payment status. Please contact us if you were charged.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/payment"
                data-testid="error-retry-button"
                className="bg-primary-main text-white hover:bg-primary-light rounded-none px-8 py-4 uppercase tracking-widest text-xs font-semibold transition-all"
              >
                Try Again
              </a>
              <a
                href="/enquiry"
                data-testid="error-contact-button"
                className="border border-primary-main text-primary-main hover:bg-primary-main hover:text-white rounded-none px-8 py-4 uppercase tracking-widest text-xs font-semibold transition-all"
              >
                Contact Support
              </a>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="pt-16 min-h-screen flex items-center justify-center bg-background-main py-20">
      <div className="max-w-3xl mx-auto px-6">
        {renderContent()}
      </div>
    </div>
  );
};

export default PaymentSuccess;